/**
 * ⭐⭐⭐ **Mesin promo — kode BERSAMA portal & perangkat.**
 *
 * ### ⛔ Kenapa ia dipindah ke sini (2026-08-25)
 * Hitungan diskon & masa berlaku dulu tinggal sebagai method privat di `PromoService`
 * (`pos-core`), yang hanya bisa dijalankan di atas basis data. Akibatnya tier satu-tablet **tak
 * bisa memakai promo saat luring sama sekali**: perakitan lokal berhenti begitu promo dipakai.
 *
 * ⛔⛔ Dan menyalinnya ke klien adalah jawaban yang paling menggoda dan paling salah: tier tablet
 * menghitung sendiri lalu server **menghitung ulang dan MENOLAK selisih** (`SYNC-409-VARIANCE`).
 * Dua salinan aturan pembulatan berarti tiap penjualan berpromo ditolak — fiturnya tak "agak
 * salah", ia mati total, dan matinya baru terlihat di outlet.
 *
 * ⇒ Satu fungsi, dipanggil dua sisi. Pola yang sama dengan `tax-engine` dan `combo-allocation`.
 *
 * ⚠️ Ia sengaja **murni**: tak menyentuh SQL, tak tahu apa-apa soal pesanan. Yang menentukan
 * baris mana yang layak tetap di pemanggil — di server lewat `promo_target`, di klien lewat
 * katalog yang membawanya turun.
 */
import { money, moneyToWire, mulMoneyByRate, rate } from '@osbpos/contract';
/**
 * Masa berlaku: tanggal, jam, dan hari.
 *
 * ⚠️ `null` = tanpa batas — ⛔ bukan "00:00–23:59". Menyimpan rentang penuh sebagai ganti `null`
 * membuat "promo ini memang berjadwal?" tak bisa dijawab tanpa menebak maksud angkanya.
 *
 * ⛔ Mengembalikan ALASAN, tak melempar: pemanggil di server menerjemahkannya jadi `AppError`
 * ber-kode, pemanggil di layar jadi kalimat. Fungsi murni yang melempar memaksa keduanya menangkap
 * pengecualian untuk sesuatu yang sepenuhnya bisa diperkirakan.
 */
export function promoValidity(spec, businessDate, now) {
    if (spec.validFrom != null && businessDate < spec.validFrom)
        return 'not_started';
    if (spec.validTo != null && businessDate > spec.validTo)
        return 'expired';
    if (spec.validDays != null && spec.validDays.length > 0) {
        /* ISO 1=Senin … 7=Minggu — `getDay()` memberi 0 untuk Minggu, jadi 0 dipetakan ke 7. */
        const hari = now.getDay() === 0 ? 7 : now.getDay();
        if (!spec.validDays.includes(hari))
            return 'wrong_day';
    }
    if (spec.validTimeFrom != null && spec.validTimeTo != null) {
        const jam = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const dari = spec.validTimeFrom.slice(0, 5);
        const sampai = spec.validTimeTo.slice(0, 5);
        if (jam < dari || jam > sampai)
            return 'wrong_time';
    }
    return null;
}
/**
 * Berapa potongannya atas `dasar` — jumlah baris yang LAYAK, bukan seluruh bill.
 *
 * ⛔ Tak pernah melebihi dasarnya: bill bernilai negatif bukan diskon, itu pengeluaran kas.
 */
export function promoDiscount(spec, dasar) {
    if (spec.kind === 'percent') {
        /*
         * ⚠️ `mulMoneyByRate`, ⛔ bukan perkalian biasa: RATE berskala 6 dan MONEY berskala 4, dan
         * pembulatannya harus sama dengan mesin pajak. Dua cara membulatkan di satu sistem berarti dua
         * jawaban untuk pertanyaan yang sama — dan di sini bedanya satu rupiah cukup untuk membuat
         * SETIAP penjualan tablet berpromo ditolak.
         */
        const kasar = mulMoneyByRate(dasar, rate(spec.percentRate ?? '0'), 'half_up');
        const batas = spec.maxDiscount == null ? kasar : money(spec.maxDiscount);
        const dipakai = kasar <= batas ? kasar : batas;
        return dipakai <= dasar ? dipakai : dasar;
    }
    const nominal = money(spec.amount ?? '0');
    return nominal <= dasar ? nominal : dasar;
}
/** Apakah `dasar` sudah memenuhi belanja minimum. ⚠️ Dipisah supaya pesannya bisa menyebut ANGKA. */
export function promoMeetsMinimum(spec, dasar) {
    return spec.minSubtotal == null || dasar >= money(spec.minSubtotal);
}
/** ⭐ Apakah baris berproduk `code` disasar promo ini. ⚠️ Tanpa sasaran = SEMUA (lihat `PromoSpec`). */
export function promoTargetsProduct(spec, productCode) {
    if (spec.scope === 'order')
        return true;
    const t = spec.targetProductCodes;
    return t === undefined || t.length === 0 || t.includes(productCode);
}
/** Bentuk kawat potongan — supaya pemanggil tak perlu tahu `Money` itu apa. */
export function promoDiscountWire(spec, dasar) {
    return moneyToWire(promoDiscount(spec, dasar));
}
//# sourceMappingURL=promo-engine.js.map